import { ShieldAlert } from 'lucide-react';

import { useGuardContext } from '@/hooks/useGuardContext';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * LockdownBanner — Full-width red alert banner displayed when an emergency
 * lockdown is active on the guard's property.
 *
 * Shown below the nav header alongside OfflineBanner. When active, all QR
 * scans and PIN entries will fail-closed at the server — this banner ensures
 * the guard understands why entries are being denied rather than seeing
 * opaque error messages.
 *
 * Requirements: Emergency module, fail-closed gate validation (ADR-004)
 */
export function LockdownBanner() {
  const { t } = useTranslation();
  const { isLockdown, lockdownType } = useGuardContext();

  if (!isLockdown) return null;

  const typeLabel = lockdownType
    ? lockdownType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

  const message = typeLabel
    ? t('guard.lockdown.bannerWithType').replace('{type}', typeLabel)
    : t('guard.lockdown.banner');

  return (
    <div
      className="flex items-center gap-2 bg-[var(--status-red)] px-4 py-2.5 text-sm font-semibold text-white"
      role="alert"
      aria-live="assertive"
    >
      <ShieldAlert className="size-4 shrink-0" aria-hidden />
      <span>{message}</span>
    </div>
  );
}
